'use client';

import React, { Fragment, useMemo, useState } from 'react';
import { Transition } from '@headlessui/react';
import { useForm, Controller } from 'react-hook-form';
import Cookies from 'js-cookie';
import { Localization } from '@/integrations/crystallize/types';
import { constructURL } from '@/integrations/crystallize/language-and-market';
import { LanguageCountryList } from '@/integrations/crystallize/types/localization';
import {
  countryLanguagesLimitations,
  DEFAULT_LOCALIZATION,
  fallbackCountry,
} from '@/integrations/i18n/settings';
import Flag from 'react-world-flags';
import {
  mapCountryCode,
  getCountryName,
  getPathWithoutLocalePrefix,
  removePathPrefix,
  getLanguageName,
} from '@/ui/utils/localization-helpers/localization';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { createTranslator, type Messages } from 'next-intl';
import { LOCALIZATION_COOKIE_NAME, LOCALIZATION_SAVED_COOKIE_NAME } from '@/utils/constants';
import { urlParameterCodes } from '@/constants/urlParams';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { useHreflang } from '@/ui/providers/hrefLangContext';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import Button from '../../shared/Button';
import { SelectInput } from '../../shared/inputs/Dropdown/SelectInput';
import internationalIcon from '../../../../../public/images/international-icon.svg';

// Retrieve and parse the localization cookie
const getLocalizationFromCookies = ():
  | { localizationFromCookies: Localization | undefined; localizationSaved: boolean | undefined }
  | undefined => {
  try {
    const localizationFromCookies = Cookies.get(LOCALIZATION_COOKIE_NAME);
    const localizationSaved = Cookies.get(LOCALIZATION_SAVED_COOKIE_NAME);
    return {
      localizationFromCookies: localizationFromCookies
        ? JSON.parse(localizationFromCookies)
        : undefined,
      localizationSaved: localizationSaved ? localizationSaved === 'true' : undefined,
    };
  } catch (error) {
    console.error(
      'Error getting localization from cookies: Failed to parse localization cookies',
      error,
    );
    return undefined;
  }
};

const dontShowSelectorPages = ['/direct-purchase'];

export default function LanguageCountrySelector({
  languageCountryList,
  chooserUiLanguage,
  chooserUiMessages,
}: Readonly<{
  languageCountryList: LanguageCountryList;
  chooserUiLanguage: string;
  chooserUiMessages: Messages;
}>) {
  const isHydrated = useIsHydrated();
  const currentPath = usePathname();

  const { hreflangs } = useHreflang();

  // Translate the chooser's own copy in the visitor's preferred language (resolved server-side:
  // browser -> geo-IP -> en), independent of the page's URL locale.
  const t = useMemo(
    () =>
      createTranslator({
        locale: chooserUiLanguage,
        messages: { LanguageCountrySelector: chooserUiMessages },
        namespace: 'LanguageCountrySelector',
      }),
    [chooserUiLanguage, chooserUiMessages],
  );

  const localizationCookies = getLocalizationFromCookies();

  const preselectedLocalization =
    localizationCookies?.localizationFromCookies || DEFAULT_LOCALIZATION;

  const localizationSaved = localizationCookies?.localizationSaved;

  const { control, handleSubmit, watch } = useForm<Localization>({
    defaultValues: preselectedLocalization,
  });

  const isPathWithoutLanguageSelector = dontShowSelectorPages.some((noSelectorPage) =>
    currentPath.includes(noSelectorPage),
  );
  const showSelector = isPathWithoutLanguageSelector ? false : !localizationSaved;

  // Open the overlay if cookie is not saved
  const [open, setOpen] = useState(showSelector);

  const selectedCountry = watch('country');

  const handleSave = (data: Localization) => {
    Cookies.set(LOCALIZATION_COOKIE_NAME, JSON.stringify(data));
    Cookies.set(LOCALIZATION_SAVED_COOKIE_NAME, 'true', { sameSite: 'none', secure: true });
    Cookies.set(LOCALIZATION_COOKIE_NAME, JSON.stringify(data), { sameSite: 'none', secure: true });

    // take current path without localization prefix (locale-less deep links are rewritten by
    // the middleware, so the prefix may be absent from the address bar)
    let pathname = getPathWithoutLocalePrefix(globalThis.location.pathname);

    // try to find alternative path in hreflang alternate paths, if translated slug exists
    const hreflangPath = hreflangs?.find((hreflang) => hreflang.language === data.language)?.path;

    if (hreflangPath) {
      pathname = removePathPrefix(hreflangPath);
    }

    const newUrl = constructURL(new URL(globalThis.location.href), data, pathname);

    if (newUrl !== globalThis.location.href) {
      // A locale switch is not a distinct content pageview: mark this forced reload with a one-shot
      // param so the QualifiedUser pageview counter skips it (and strips it) on the next load.
      const targetUrl = new URL(newUrl);
      targetUrl.searchParams.set(urlParameterCodes.localeSwitch, '1');
      globalThis.location.href = targetUrl.toString(); // hard refresh
    }

    setOpen(false);
  };

  let selectedCountryName: string = '';
  if (selectedCountry === '001') {
    selectedCountryName = t('InternationalCountry');
  } else {
    selectedCountryName = getCountryName(selectedCountry || fallbackCountry, chooserUiLanguage);
  }

  // check if country has limitations for languages

  const availableLanguages = languageCountryList.languages.filter((lang) => {
    const countryHasLimitations =
      countryLanguagesLimitations[
        (selectedCountry || fallbackCountry) as keyof typeof countryLanguagesLimitations
      ];
    if (countryHasLimitations) {
      return countryHasLimitations.includes(lang.code);
    }
    return true;
  });

  if (!isHydrated) {
    return null;
  }

  const visibleCountries = languageCountryList.countries.filter((country) =>
    country?.countryVisibility?.includes('selector'),
  );

  const countryOptions = visibleCountries.map((country) => {
    if (country.code === '001') {
      return {
        value: country.code,
        label: t('InternationalCountry'),
        prependItem: (
          <span className="relative mr-2 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-cover p-0.5">
            <Image
              src={internationalIcon}
              alt={t('InternationalCountry')}
              className="object-contain"
            />
          </span>
        ),
      };
    }
    const label = getCountryName(country.code, chooserUiLanguage) || country.code;

    const flag = (
      <span className="mr-2 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-cover p-0.5">
        <Flag
          code={mapCountryCode(country.code)}
          className="h-full w-full object-cover"
          alt={label}
        />
      </span>
    );
    return {
      value: country.code,
      label,
      prependItem: flag,
    };
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('Select Language and Country')}
      >
        <div className="flex gap-2">
          <span className="relative mr-2 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-cover">
            {selectedCountry === '001' ? (
              <Image
                src={internationalIcon}
                alt={t('InternationalCountry')}
                className="object-contain"
              />
            ) : (
              <Flag
                code={mapCountryCode(selectedCountry || fallbackCountry)}
                className="h-full w-full object-cover"
                alt={selectedCountryName}
              />
            )}
          </span>
          <p>{selectedCountryName}</p>
        </div>
      </button>
      <Transition.Root show={open} as={Fragment}>
        <div className="relative z-80">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-100 bg-opacity-50 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div className="relative w-full  max-w-xl transform rounded-2xl bg-white px-4  py-5 text-left text-neutral-100 shadow-xl transition-all sm:my-8 sm:p-6 md:p-10">
                  <button
                    type="button"
                    className="absolute right-1 top-1 p-1 text-neutral-50"
                    onClick={() => setOpen(false)}
                    aria-label={t('Close')}
                  >
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>

                  <form onSubmit={handleSubmit(handleSave)}>
                    <div>
                      <div>
                        <h3 className="text-center text-lg font-semibold leading-6">
                          {t('Select Language and Country')}
                        </h3>

                        <p className="mt-6 text-center text-sm">
                          {t(
                            'Select a different country or region to view content for your location',
                          )}
                        </p>

                        <div className="mt-6">
                          <label htmlFor="countrySelect" className="mb-1 block text-sm font-medium">
                            {t('Choose your country')}
                          </label>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: 'Country is required' }}
                            render={({ field }) => (
                              <SelectInput
                                name={field.name}
                                control={control}
                                placeholder={t('Select a country')}
                                options={countryOptions}
                              />
                            )}
                          />
                        </div>
                        <div className="mt-4">
                          <label
                            htmlFor="languageSelect"
                            className="mb-1 block text-sm font-medium"
                          >
                            {t('Choose your language')}
                          </label>
                          <Controller
                            name="language"
                            control={control}
                            rules={{ required: t('Language is required') }}
                            render={({ field }) => (
                              <SelectInput
                                name={field.name}
                                control={control}
                                options={availableLanguages.map((lang) => ({
                                  value: lang.code,
                                  label: getLanguageName(lang.code) || lang.name,
                                }))}
                                placeholder={t('Select a language')}
                              />
                            )}
                          />
                        </div>
                        <Button type="submit" className="mt-8 h-11 w-full">
                          {t('Save')}
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Transition.Root>
    </>
  );
}
